'use client';

import { useState, useEffect } from 'react';
import { addTrackTag, removeTrackTag } from '@fe-news/api/member';
import { fetchUserTagIsTracked } from '@fe-news/actions/member';
import { useQuery, useMutation } from '@tanstack/react-query';

type UseTrackTagsProps = {
  tagId: string;
  isLogin: boolean;
  isUserNoTracked?: boolean;
};

const useTrackTags = ({ tagId, isLogin, isUserNoTracked = false }: UseTrackTagsProps) => {
  const [isSaved, setIsSaved] = useState(false);

  const {
    data: userTrackedData,
    isLoading: userTrackedLoading,
    refetch: refetchIsTracked
  } = useQuery({
    queryKey: ['userTagIsTracked', `isLogin:${isLogin}`, `isUserNoTracked:${isUserNoTracked}`],
    queryFn: () => fetchUserTagIsTracked(isLogin)
  });

  const { isPending: addTagIsLoading, mutateAsync: addTagMutate } = useMutation({
    mutationFn: (tagId: string) => {
      setIsSaved(true);
      return addTrackTag(tagId);
    },
    onSuccess: () => {
      setIsSaved(true);
      refetchIsTracked();
    },
    onError: () => {
      setIsSaved(false);
    }
  });

  const { isPending: removeTagIsLoading, mutateAsync: removeTagMutate } = useMutation({
    mutationFn: (tagId: string) => {
      setIsSaved(false);
      return removeTrackTag(tagId);
    },
    onSuccess: () => {
      setIsSaved(false);
      refetchIsTracked();
    },
    onError: () => {
      setIsSaved(true);
    }
  });

  const isTracked = userTrackedData?.data?.includes(tagId) ?? false;
  const isLoading = addTagIsLoading || removeTagIsLoading || userTrackedLoading;
  const myTags = userTrackedData?.data && isLogin ? userTrackedData.data : [];

  useEffect(() => {
    setIsSaved(isTracked);
  }, [isTracked]);

  return {
    isLoading,
    isTracked: isSaved,
    addTagMutate,
    removeTagMutate,
    myTags,
    refetch: refetchIsTracked
  };
};

export default useTrackTags;
